<template>
  <div class="crud-table">
    <v-dialog v-model="infoDlg" max-width="550px" scrollable>
      <v-card>
        <v-card-title class="headline">
          {{ entityName }} Details
        </v-card-title>
        <v-card-text class="px-4">
          <slot name="displayDialog" :item="currentItem" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn outline color="primary" @click="infoDlg = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-form ref="editForm" v-model="isEditValid" lazy-validation>
      <v-dialog v-model="editDlg" max-width="550px" scrollable>
        <v-card>
          <v-card-title class="headline">
            {{ mode }} {{ entityName }}
          </v-card-title>
          <v-card-text class="px-4">
            <slot name="editDialog" :item="currentItem" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn outline color="primary" @click="editDlg = false">
              Cancel
            </v-btn>
            <v-btn dark color="primary" @click="save()">
              {{ mode === 'Add' ? 'Add' : 'Save' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-form>

    <v-form ref="addForm" v-model="isAddValid" lazy-validation>
      <v-dialog v-if="hasAddDialogSlot" v-model="addDlg" max-width="550px" scrollable>
        <v-card>
          <v-card-title class="headline">
            {{ mode }} {{ entityName }}
          </v-card-title>
          <v-card-text class="px-4">
            <slot name="addDialog" :item="currentItem" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn outline color="primary" @click="addDlg = false">
              Cancel
            </v-btn>
            <v-btn dark color="primary" @click="save()">
              {{ mode === 'Add' ? 'Add' : 'Save' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-form>
    <v-alert :value="backgroundTaskRunning && !readOnly" type="warning" class="ma-0" style="height: 48px" transition="slide-y-transition">
      <div>This data is currently read-only because a background process is running. The information shown may be updated by the process.</div>
      <div><router-link :to="{ name: 'ARRP' }">Click here to view the status.</router-link></div>
    </v-alert>
    <v-layout row class="relative">
      <v-flex shrink>
        <v-data-table :items="currentPage"
                      :pagination="pagination"
                      :total-items="totalItems"
                      hide-actions>
          <template v-slot:headers="{}">
            <th class="px-0" />
          </template>
          <template v-slot:items="{item}">
            <td :class="['px-2',...(item.locked ? ['locked'] : [])]">
              <v-layout row>
                <tooltip v-if="item.errors" bottom>
                  <nt-icon slot="activator" color="error">
                    error
                  </nt-icon>
                  {{ item.errors }}
                </tooltip>
                <tooltip v-if="item.warnings" bottom>
                  <nt-icon slot="activator" color="warning">
                    warning
                  </nt-icon>
                  {{ item.warnings }}
                </tooltip>
              </v-layout>
            </td>
          </template>
          <template v-slot:no-data>
            <span />
          </template>
        </v-data-table>
      </v-flex>

      <v-flex style="overflow-x: auto">
        <v-data-table :headers="computedHeaders"
                      :items="currentPage"
                      :pagination="pagination"
                      :total-items="totalItems"
                      hide-actions>
          <template v-slot:headers="props">
            <th v-for="header in props.headers" :key="header.text"
                :width="header.width + 'px'"
                :style="{'text-align': header.align}"
                :class="getHeaderClasses(header)"
                @click="changeSort(header)">
              <v-layout align-center justify-center row fill-height :style="{'min-width': header.width + 'px'}">
                <v-flex shrink>
                  <div v-for="(txt, idx) in header.text.split('\n')" :key="idx">
                    {{ txt }}
                  </div>
                </v-flex>
                <v-flex v-if="header.sortable !== false" shrink>
                  <nt-icon slot="activator" small class="mx-1">
                    arrow_upward
                  </nt-icon>
                </v-flex>
              </v-layout>
            </th>
          </template>
          <template v-slot:items="{item}">
            <tr>
              <td v-for="header in computedHeaders" :key="header.text" :style="{'text-align': header.align}" :class="item.locked ? ['locked'] : []">
                <div class="cell">{{ applyFilter(item[header.value], header.filter) }}</div>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>

      <v-flex shrink>
        <v-data-table :headers="computedHeaders"
                      :items="currentPage"
                      :pagination="pagination"
                      :total-items="totalItems"
                      hide-actions>
          <template v-slot:headers="{}">
            <th>Actions</th>
          </template>
          <template v-slot:items="{item, index}">
            <tr :key="index">
              <td :class="item.locked ? ['locked'] : []">
                <v-layout row>
                  <tooltip bottom>
                    <nt-icon slot="activator" @click="displayItem(item)">
                      more_vert
                    </nt-icon>
                    {{ entityName }} Details
                  </tooltip>
                  <tooltip v-if="canEdit && !item.locked" bottom>
                    <nt-icon slot="activator" color="info" @click="editItem(item)">
                      edit
                    </nt-icon>
                    Edit {{ entityName }}
                  </tooltip>
                  <tooltip v-if="canDelete && !item.locked" bottom>
                    <nt-icon slot="activator" color="error" @click="deleteItem(item)">
                      delete
                    </nt-icon>
                    Delete {{ entityName }}
                  </tooltip>
                  <tooltip v-if="item.locked && !isReadOnly" bottom>
                    <nt-icon slot="activator" @click="forceUnlock(item)">
                      lock
                    </nt-icon>
                    <div>
                      <div>Item Locked for Editing.</div>
                      <div v-if="unlockEnabled">
                        Click to unlock
                      </div>
                    </div>
                  </tooltip>
                  <slot name="moreActions" :item="item" />
                </v-layout>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <span />
          </template>
        </v-data-table>
      </v-flex>
      <transition name="fade">
        <div v-show="loading" class="loadingOverlay">
          <v-progress-circular color="primary" indeterminate />
        </div>
      </transition>
    </v-layout>

    <hr />

    <v-layout row wrap class="pt-2 pb-1">
      <v-flex :class="$vuetify.breakpoint.xsOnly ? ['xs12'] : ['shrink']" class="text-xs-center text-sm-left">
        <v-btn v-if="canAdd" outline color="primary" @click="addItem()">
          <nt-icon class="mr-2">
            add
          </nt-icon>
          Add {{ entityName }}
        </v-btn>
      </v-flex>
      <v-flex v-if="!!this.$slots.customTableActions" :class="$vuetify.breakpoint.xsOnly ? ['xs12'] : ['shrink']" class="text-xs-center text-sm-left">
        <slot name="customTableActions" />
      </v-flex>
      <v-spacer />
      <v-flex :class="$vuetify.breakpoint.xsOnly ? ['xs12'] : ['shrink']" class="text-xs-center text-sm-right">
        <v-pagination :value="pagination.page" :length="pages" color="primary" total-visible="7" @input="changePage" />
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
  export default {
    props: {
      headers: { type: Array, default: () => [] },
      entityName: { type: String, default: 'Item' },
      readOnly: { type: Boolean, default: false },
      vuexModule: { type: String, default: '' },
      addEnabled: { type: Boolean, default: true },
      editEnabled: { type: Boolean, default: true },
      deleteEnabled: { type: Boolean, default: true },
      unlockEnabled: { type: Boolean, default: true }
    },
    data: () => ({
      isAddValid: false,
      isEditValid: false,
      infoDlg: false,
      editDlg: false,
      addDlg: false,
      mode: 'Add',
      currentItem: {}
    }),
    computed: {
      vuexStore() {
        return this.$store.state[this.vuexModule];
      },
      hasAddDialogSlot() {
        return !!this.$scopedSlots.addDialog;
      },
      computedHeaders() {
        return this.headers.filter(h => h.breakpoint == null ||
          h.breakpoint === 'xs' ||
          (h.breakpoint !== 'xl' ? this.$vuetify.breakpoint[`${h.breakpoint}AndUp`] : this.$vuetify.breakpoint.xlOnly))
      },
      currentPage() {
        return this.$store.getters[`${this.vuexModule}/currentPage`] || this.vuexStore.items;
      },
      backgroundTaskRunning() {
        return this.$store.getters['processingQueue/processingQueues'] && this.$store.getters['processingQueue/processingQueues'].length > 0;
      },
      isReadOnly() {
        return this.readOnly || this.backgroundTaskRunning;
      },
      canAdd() {
        return this.addEnabled && !this.isReadOnly;
      },
      canEdit() {
        return this.editEnabled && !this.isReadOnly;
      },
      canDelete() {
        return this.deleteEnabled && !this.isReadOnly;
      },
      pages() {
        return Math.ceil(this.totalItems / this.vuexStore.pagingInfo.rowsPerPage);
      },
      pagination() {
        return { ...this.vuexStore.pagingInfo };
      },
      totalItems() {
        return this.vuexStore.totalItems;
      },
      loading() {
        return this.vuexStore.loading || false;
      }
    },
    watch: {
      editDlg(after, before) {
        // if the dialog was just closed, and we're editing
        if (!after && before && this.mode === 'Edit') {
          this.unlockItem(this.currentItem);
        }
      }
    },
    mounted() {
      this.changePage(1);
      this.connect();
    },
    beforeDestroy() {
      this.disconnect();
    },
    methods: {
      applyFilter(value, filterName) {
        if (!filterName || !this.$options.filters[filterName]) {
          return value;
        }
        return this.$options.filters[filterName](value);
      },
      connect() {
        const action = `${this.vuexModule}/connect`;
        if (Object.keys(this.$store._actions).includes(action)) {
          this.$store.dispatch(action)
        }
      },
      disconnect() {
        const action = `${this.vuexModule}/disconnect`;
        if (Object.keys(this.$store._actions).includes(action)) {
          this.$store.dispatch(action)
        }
      },
      displayItem(item) {
        this.currentItem = { ...item };
        this.infoDlg = true;
      },
      editItem(item) {
        this.currentItem = { ...item };
        this.lockItem(this.currentItem);
        this.mode = 'Edit';
        this.editDlg = true;
      },
      deleteItem(item) {
        this.currentItem = { ...item };
        this.mode = 'Delete';
        this.lockItem(this.currentItem);
        this.$confirm({
          title: `Delete ${this.entityName}?`,
          text: `Are you sure you want to delete this ${this.entityName}?`,
          yesLabel: `Delete`,
          yesColor: `error`
        }).then(x => {
          if (x) {
            this.$store.dispatch(`${this.vuexModule}/deleteItem`, this.currentItem)
              .then(() => { this.$snackbar(`${this.entityName} Deleted`); })
              .catch(err => this.$snackbar(err.message, 'error'));
          }
          this.unlockItem(item);
        });
      },
      addItem() {
        this.currentItem = {};
        this.mode = 'Add';
        if (this.hasAddDialogSlot) {
          this.addDlg = true;
        } else {
          this.editDlg = true;
        }
      },
      lockItem(item) {
        this.$store.dispatch(`${this.vuexModule}/lockItem`, item);
      },
      unlockItem(item) {
        this.$store.dispatch(`${this.vuexModule}/unlockItem`, item);
      },
      forceUnlock(item) {
        if (this.unlockEnabled) {
          this.$confirm({
            title: 'Unlock Row for Editing?',
            yesLabel: 'Unlock',
            text: 'This item has been locked because it is being edited in another session. Overriding this lock may produce unexpected results when saving. Do you wish to unlock this item to allow editing or deleting?'
          }).then(x => {
            if (x) {
              this.unlockItem(item);
            }
          })
        }
      },
      save() {
        if ((this.addDlg && this.$refs.addForm.validate()) || (this.editDlg && this.$refs.editForm.validate())) {
          this.$store.dispatch(`${this.vuexModule}/item${this.mode}ed`, this.currentItem);
          this.editDlg = false;
          this.addDlg = false;
        }
      },
      changeSort(header) {
        if (header.sortable !== false) {
          const column = header.value;
          if (column === this.vuexStore.pagingInfo.sortBy) {
            this.$store.dispatch(this.vuexModule + '/sortItems', { sortBy: column, descending: !this.vuexStore.pagingInfo.descending });
          } else {
            this.$store.dispatch(this.vuexModule + '/sortItems', { sortBy: column, descending: false });
          }
        }
      },
      changePage(newPage) {
        this.$store.dispatch(this.vuexModule + '/getPagedData', newPage);
      },
      getHeaderClasses(header) {
        return [
          'column',
          ...(header.sortable !== false ? [
            'sortable',
            this.pagination.descending ? 'desc' : 'asc',
            header.value === this.pagination.sortBy ? 'active' : ''
          ] : [])
        ];
      }
    }
  }
</script>

<style scoped>
  table.v-table tbody td, table.v-table thead th {
    text-align: center;
    padding-left: 12px;
    padding-right: 12px;
  }

  .cell {
    padding: 3px 0;
    max-height: 47px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow-y: hidden;
  }

  .locked {
    background-color: lightgrey;
    color: black;
  }

  .relative {
    position: relative;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity .5s
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0
  }

</style>
